"use client";
import { useEffect, useMemo, useState } from "react";
import { calculateRelativeTime } from "~/lib/formatDate";

import type { TypographyProps } from "@mui/material/Typography";
import Typography from "@mui/material/Typography";

/**
 * Component that displays relative time (e.g., "2 hours ago", "in 5 minutes")
 * Updates automatically at optimal intervals based on time difference:
 * - Every second for recent times (< 1 minute)
 * - Every minute for times up to hours
 * - Every hour for longer periods
 */
interface DateTimeRelativeProps extends Omit<TypographyProps, "children"> {
  date: string | Date | null | undefined;
  prefix?: string;
  suffix?: string;
}

export default function DateTimeRelative({
  date,
  prefix = "",
  suffix = "",
  ...typographyProps
}: DateTimeRelativeProps) {
  const [now, setNow] = useState(() => new Date());

  const relativeTime = useMemo(
    () => calculateRelativeTime(date, { now }),
    [date, now],
  );
  const updateInterval = useMemo(
    () => calculateUpdateInterval(date, now),
    [date, now],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  return (
    <Typography variant="inherit" component="span" {...typographyProps}>
      {prefix}
      {relativeTime}
      {suffix}
    </Typography>
  );
}

function calculateUpdateInterval(
  date: string | Date | null | undefined,
  now: Date,
): number {
  if (!date) return 60000; // Default to 1 minute

  const targetDate = new Date(date);
  const absDiffMs = Math.abs(now.getTime() - targetDate.getTime());

  const absDiffSeconds = Math.floor(absDiffMs / 1000);
  const absDiffMinutes = Math.floor(absDiffSeconds / 60);
  const absDiffHours = Math.floor(absDiffMinutes / 60);

  // Update every second for recent times (seconds)
  if (absDiffSeconds < 60) {
    return 1000;
  }
  // Update every minute for times up to hours
  else if (absDiffMinutes < 60) {
    return 60000;
  }
  // Update every hour for times up to days
  else if (absDiffHours < 24) {
    return 3600000;
  }
  // Update every hour for longer periods (days, weeks, months, years)
  else {
    return 3600000;
  }
}
